(() => {
/** Shared promotion policy. A private property distinguishes gifts from purchased liners. */
const GIFT_PROPERTY = '_wb_free_liner';
const GIFT_VALUE = '2026-10-12';
function isGift(item, variantIds) {
  return variantIds.includes(Number(item.variant_id)) && item.properties?.[GIFT_PROPERTY] === GIFT_VALUE;
}
function eligibleSubtotal(cart, config) {
  if (config.afterDiscount) {
    // Derive merchandise only: an Ajax total observed after checkout included shipping.
    const cents = value => {
      if (typeof value !== 'number' && !(typeof value === 'string' && /^\d+$/.test(value))) return null;
      const amount = Number(value);
      return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
    };
    if (!Array.isArray(cart.items)) return 0;
    let paid = 0;
    for (const item of cart.items) {
      const amount = cents(item.final_line_price);
      if (amount === null) return 0;
      if (item.properties?.[GIFT_PROPERTY] !== GIFT_VALUE) paid += amount;
    }
    // These lists describe the same order discount; subtract one representation once.
    const applications = cart.cart_level_discount_applications;
    const allocations = cart.cart_level_discount_allocations;
    const useApplications = Array.isArray(applications) && applications.length > 0;
    const discounts = useApplications ? applications : (Array.isArray(allocations) ? allocations : []);
    for (const discount of discounts) {
      const amount = cents(useApplications ? discount.total_allocated_amount : discount.amount);
      if (amount === null) return 0;
      paid -= amount;
    }
    return Number.isSafeInteger(paid) ? Math.max(0,paid) : 0;
  }
  return cart.items.reduce((sum, item) => sum + (isGift(item, config.variantIds) ? 0 : Number(
    config.afterDiscount ? item.final_line_price : (item.original_line_price ?? item.line_price)
  )), 0);
}
function qualifies(cart, config) {
  const subtotal = eligibleSubtotal(cart, config);
  return config.strictlyAbove ? subtotal > config.thresholdCents : subtotal >= config.thresholdCents;
}
function giftIsFree(item) {
  return Number(item.final_line_price) === 0 && Number(item.original_line_price) > 0 &&
    (item.line_level_discount_allocations || []).some(allocation => Number(allocation.amount) > 0);
}
function changesNeeded(cart, config) {
  const gifts = cart.items.filter(item => isGift(item, config.variantIds));
  const keep = config.enabled && qualifies(cart, config) && cart.attributes?._gift_opt_out !== 'true';
  return gifts.flatMap((item, index) => {
    const quantity = keep && index === 0 ? 1 : 0;
    return item.quantity === quantity ? [] : [{id: item.key, quantity}];
  });
}

window.WBFreeLinerPolicy={GIFT_PROPERTY,GIFT_VALUE,isGift,eligibleSubtotal,qualifies,giftIsFree,changesNeeded};
})();
/* Installed only on review theme copies. Shopify must apply the real gift discount. */
(() => {
  const config = window.WB_FREE_LINER_CONFIG;
  if (!config?.enabled || !window.WBCart) return;
  const api = window.WBCart;
  let syncing = false;
  let scheduled = false;
  let allowCheckout = false;
  let latestCart;
  const policy = window.WBFreeLinerPolicy;
  const changeCart = api.changeCart.bind(api);
  api.changeCart = async (payload, options) => {
    if (!syncing && Number(payload.quantity) === 0) {
      const before = await api.getCart();
      const line = before.items.find(item => item.key === payload.id);
      if (line && policy.isGift(line,config.variantIds)) {
        await api.updateCart({attributes:{_gift_opt_out:'true'}},{emitUpdate:false});
      }
    }
    return changeCart(payload,options);
  };
  const selectedVariant = cart => Number(cart.attributes?._gift_selected_variant_id) || config.variantIds[0];
  async function sync() {
    if (syncing) return latestCart;
    syncing = true;
    try {
      let cart = await api.getCart();
      const signature = value => JSON.stringify(value.items.filter(item=>policy.isGift(item,config.variantIds)).map(item=>[item.variant_id,item.quantity,item.final_line_price]));
      const beforeGift = signature(latestCart || cart);
      if (!policy.qualifies(cart,config) && cart.attributes?._gift_opt_out === 'true') {
        await api.updateCart({attributes:{_gift_opt_out:''}},{emitUpdate:false});
        // Mutation responses use a different discount shape from cart.js.
        cart = await api.getCart();
      }
      const changes = policy.changesNeeded(cart, config);
      for (const change of changes) await api.changeCart(change, {emitUpdate:false});
      if (changes.length) cart = await api.getCart();
      const gifts = cart.items.filter(item => policy.isGift(item, config.variantIds));
      if (!gifts.length && policy.qualifies(cart, config) && cart.attributes?._gift_opt_out !== 'true') {
        const product = await api.fetchProductJson(config.handle);
        const variants = product.variants || product.product?.variants || [];
        const chosen = selectedVariant(cart);
        const candidateIds = [chosen, ...config.variantIds.filter(id => id !== chosen)];
        const available = candidateIds.find(id => variants.some(variant => Number(variant.id) === id && variant.available));
        if (available) {
          await api.addItems([{id:available,quantity:1,properties:{[policy.GIFT_PROPERTY]:policy.GIFT_VALUE}}], {emitUpdate:false});
          cart = await api.getCart();
        }
      }
      latestCart = cart;
      render(cart);
      api.dispatchUpdated(cart);
      if (config.locale==='de-DE' && /\/cart\/?$/.test(window.location?.pathname || '') && beforeGift!==signature(cart)) {
        window.location.reload();
      }
      return cart;
    } finally { syncing = false; }
  }
  function schedule() {
    if (syncing || scheduled) return;
    scheduled = true;
    api.enqueue(async () => {
      try { await sync(); } catch(error) { console.error('Gift reconciliation failed', error); }
      finally { scheduled = false; }
    });
  }
  function render(cart) {
    const gift = cart.items.find(item => policy.isGift(item, config.variantIds));
    document.querySelectorAll('form[data-cart-view], form.wb-fr-cart, form[action*="/cart"]').forEach(form => {
      let panel = form.querySelector('[data-wb-free-liner]');
      if (!panel) {
        panel = document.createElement('div');
        panel.dataset.wbFreeLiner = '';
        panel.className = 'wb-free-liner-panel';
        const checkout = form.querySelector('[name="checkout"],button[type="submit"]');
        (checkout?.parentElement || form).prepend(panel);
      }
      panel.replaceChildren();
      const status = document.createElement('p');
      status.className = 'wb-free-liner-status';
      status.setAttribute('aria-live','polite');
      const amountLeft = Math.max(0,config.thresholdCents-policy.eligibleSubtotal(cart,config));
      status.textContent = !gift && cart.attributes?._gift_opt_out==='true' ? config.labels.optedOut : gift ? (policy.giftIsFree(gift) ? config.labels.received : config.labels.unconfirmed) :
        (policy.qualifies(cart,config) ? config.labels.available : config.labels.progress.replace('{amount}',new Intl.NumberFormat(config.locale,{style:'currency',currency:cart.currency || 'EUR'}).format(amountLeft/100)));
      panel.append(status);
      if (!gift && cart.attributes?._gift_opt_out==='true' && policy.qualifies(cart,config)) {
        const restore=document.createElement('button');restore.type='button';restore.className='wb-free-liner-restore';restore.textContent=config.labels.restore;
        restore.addEventListener('click',()=>{restore.disabled=true;api.enqueue(async()=>{await api.updateCart({attributes:{_gift_opt_out:''}},{emitUpdate:false});await sync()}).catch(error=>{restore.disabled=false;console.error(error)})});
        panel.append(restore);
      }
      if (!gift) return;
      const label = document.createElement('label');
      label.className = 'wb-free-liner-label';
      const labelText = document.createElement('span');
      labelText.textContent = config.labels.choose;
      label.append(labelText);
      const select = document.createElement('select');
      select.className = 'wb-free-liner-select';
      select.setAttribute('aria-label',config.labels.choose);
      config.variantIds.forEach(id => {const option=document.createElement('option');option.value=id;option.textContent=config.shades[String(id)];option.selected=id===Number(gift.variant_id);select.append(option)});
      select.addEventListener('change', () => {
        select.disabled = true;
        api.enqueue(async () => {
          const fresh = await api.getCart();
          const current = fresh.items.find(item => policy.isGift(item,config.variantIds));
          // Add replacement before removing the old gift so an unavailable shade preserves the gift.
          await api.addItems([{id:Number(select.value),quantity:1,properties:{[policy.GIFT_PROPERTY]:policy.GIFT_VALUE}}],{emitUpdate:false});
          const afterAdd = await api.getCart();
          const oldLine = afterAdd.items.find(item => item.variant_id===current?.variant_id && policy.isGift(item,config.variantIds));
          if(oldLine)await api.changeCart({id:oldLine.key,quantity:0},{emitUpdate:false});
          await api.updateCart({attributes:{_gift_selected_variant_id:select.value,_gift_opt_out:''}},{emitUpdate:false});
          await sync();
        }).catch(error => {status.textContent=config.labels.unavailable;select.disabled=false;console.error(error)});
      });
      label.append(select);panel.append(label);
    });
  }
  window.addEventListener('cart:updated',schedule);
  window.addEventListener('update-minicart',schedule);
  document.addEventListener('click',event=>{
    const link=event.target.closest?.('a[href*="/cart/change"]');
    const row=link?.closest('[data-wb-gift-key]');
    if(!row || !/[?&]quantity=0(?:&|$)/.test(link.href))return;
    event.preventDefault();
    api.enqueue(async()=>{
      await api.changeCart({id:row.dataset.wbGiftKey,quantity:0});
      await sync();
      if(/\/cart\/?$/.test(window.location?.pathname || ''))window.location.reload();
    }).catch(error=>console.error('Gift removal failed',error));
  },true);
  document.addEventListener('submit',event => {
    const form = event.target;
    if (allowCheckout || !(form instanceof HTMLFormElement) || !/checkout/.test(form.action + (event.submitter?.name || ''))) return;
    event.preventDefault();event.stopImmediatePropagation();
    api.enqueue(async () => {
      const cart = await sync();
      const gifts = cart.items.filter(item => policy.isGift(item,config.variantIds));
      if (gifts.some(item => !policy.giftIsFree(item))) throw new Error(config.labels.unconfirmed);
      allowCheckout=true;
      try { form.requestSubmit(event.submitter); }
      finally { allowCheckout=false; }
    }).catch(error => {console.error('Gift checkout verification failed',error);window.alert(config.labels.unconfirmed)});
  },true);
  window.WBGift = {sync,schedule,isGift:item=>policy.isGift(item,config.variantIds),isFree:policy.giftIsFree};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',schedule);else schedule();
})();
